import TurnStileWidget from '@/components/TurnstileWidget'
import PageLayout from '@/compositions/PageLayout'
import { rbp } from '@/constants/measured-scope'
import { useAi } from '@/context/ai'
import { AnonymousChatProvider } from '@/context/anonymousChatContext'
import { getConfig } from '@/lib/config'
import { getClientIdentityForQuery, getPartnerAttributionStateFromCookies } from '@/service/request'
import AiContainer from '@/widgets/v2/AI/Container'
import { useMediaQuery } from '@react-hookz/web'
import { setCookie } from 'cookies-next'
import type { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useState } from 'react'

interface Props {
  authToken?: string
  partnerId?: string
  hideIntroVideo?: boolean
}

const HelenPage: NextPage = (props: Props) => {
  const { publicRuntimeConfig } = getConfig()
  const [isValidated, setIsValidated] = useState(false)
  const isMobile = useMediaQuery(`only screen and (max-width : ${rbp.xs})`, { initializeWithValue: false })
  const { state } = useAi()
  const footerVisible = !isMobile && !state.query.length

  if (!props.authToken && !isValidated && publicRuntimeConfig.cloudFlareTurnStile.siteKey) {
    // Show Turnstile widget if the user is not logged in and not validated
    return (
      <PageLayout footerVisible={footerVisible}>
        <TurnStileWidget
          setIsValidated={setIsValidated}
          turnStileSiteKey={publicRuntimeConfig.cloudFlareTurnStile.siteKey}
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout footerVisible={footerVisible}>
      <AnonymousChatProvider>
        <AiContainer authToken={props.authToken} partnerId={props.partnerId} hideIntroVideo={props.hideIntroVideo} />
      </AnonymousChatProvider>
    </PageLayout>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ locale = 'en-US', req, res }) => {
  const { authLocalCookie, authFacebookCookie } = getClientIdentityForQuery(req)
  const partnerState = getPartnerAttributionStateFromCookies(req.cookies)

  const HELEN_PAGE_VISITED = 'helen_page_visited'
  let hideIntroVideo = false
  const visits = Number(req.cookies[HELEN_PAGE_VISITED] || '0') + 1
  setCookie(HELEN_PAGE_VISITED, visits.toString(), {
    req,
    res,
    path: '/',
    domain: getConfig().publicRuntimeConfig.auth.authCookieDomain,
    maxAge: 60 * 60 * 24 * 365,
  })
  const watched = req.cookies['helen_intro_watched'] === 'true'
  // Hide intro video if the user has visited more than 3 times or has already watched the video
  if (visits > 3 || watched) {
    hideIntroVideo = true
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'classes'])),
      authToken: authLocalCookie || authFacebookCookie,
      partnerId: partnerState ? partnerState.id : null,
      disableAuthPrompt: true,
      persistentAuthPrompt: true,
      hideIntroVideo,
    },
  }
}

export default HelenPage
